<script setup lang="ts">
import { computed, type PropType } from 'vue'
import SpinnerLoader from '@/components/common/SpinnerLoader.vue'
import { ButtonType, ButtonVariant } from '@/components/common/crd-button/crd-button.enums.ts'

const props = defineProps({
  variant: {
    type: String as PropType<ButtonVariant>,
    default: ButtonVariant.Primary,
    validator: (value: ButtonVariant) => Object.values(ButtonVariant).includes(value),
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String as PropType<ButtonType>,
    default: ButtonType.Button,
    validator: (value: ButtonType) => Object.values(ButtonType).includes(value),
  },
})

const classes = computed(() => {
  const baseClasses = [
    'rounded-md',
    'px-3',
    'py-2',
    'text-sm',
    'font-semibold',
    'shadow-sm',
    'focus-visible:outline',
    'focus-visible:outline-2',
    'focus-visible:outline-offset-2',
    'disabled:bg-gray-400',
    'disabled:text-gray-300',
    'disabled:cursor-not-allowed',
    'flex',
    'items-center',
    'justify-center',
    'gap-2',
  ]

  const variantClasses = {
    [ButtonVariant.Cancel]: ['bg-white', 'text-gray-900', 'outline 1', 'outline-gray-300', 'hover:bg-yellowCustom'],
    [ButtonVariant.Save]: ['bg-indigo-500', 'text-white', 'hover:bg-indigo-600', 'focus-visible:outline-indigo-600'],
    [ButtonVariant.Danger]: ['bg-red-600', 'text-white', 'hover:bg-red-500', 'focus-visible:outline-red-600'],
    [ButtonVariant.Primary]: ['bg-red-600', 'text-white', 'hover:bg-red-500', 'focus-visible:outline-red-600'],
  }[props.variant] || ['bg-red-600', 'text-white', 'hover:bg-red-500', 'focus-visible:outline-red-600']

  return [...baseClasses, ...variantClasses].join(' ')
})

</script>

<template>
  <button
    :class="classes"
    :type="type"
    :disabled="disabled || isLoading"
  >
    <SpinnerLoader v-if="isLoading" />
    <slot />
  </button>
</template>
